import { FC, useEffect, useState } from 'react';
import useSWR, { Fetcher } from 'swr';
import { shallow } from 'zustand/shallow';

import { getCheckouts } from '@/data/CheckoutApi/CheckoutApi';
import { CheckoutApiResponse } from '@/data/CheckoutApi/CheckoutApi.types';
import { productDetailPayloadToBaseCarData } from '@/data/ProductApi/ProductDetailPayloadToBaseCarData';
import { getCarDetail } from '@/data/StaticPageGeneration/PagePdpDefaults';
import { authStore } from '@/stores/authStore';
import { cartStore } from '@/stores/cartStore';

const DEFAULT_FOCUS_THROTTLE = 60 * 1000;

export const CartManager: FC = () => {
  const { isUserLoggedIn, userAuthDataLoading } = authStore(
    (state) => ({
      isUserLoggedIn: state.isUserLoggedIn,
      userAuthDataLoading: state.userAuthDataLoading,
    }),
    shallow,
  );
  const { apiCartData, setCarDataLoading, setCarData, setApiCartDataLoading, setApiCartData, clearCartData } =
    cartStore(
      (state) => ({
        apiCartData: state.apiCartData,
        setCarDataLoading: state.setCarDataLoading,
        setCarData: state.setCarData,
        setApiCartDataLoading: state.setApiCartDataLoading,
        setApiCartData: state.setApiCartData,
        clearCartData: state.clearCartData,
      }),
      shallow,
    );
  const [cartLoadedFirstTime, setCartLoadedFirstTime] = useState<boolean>(false);
  const [apiCartDataSku, setApiCartDataSku] = useState<string | null>(null);

  const loadApiCartData: Fetcher<CheckoutApiResponse[] | undefined, {}> = async () => {
    // Checkout API call
    try {
      const result = await getCheckouts();
      setApiCartData(result?.length ? result[0] : null);
      return result;
    } catch (e) {
      // In case of error set the API cart data to null
      console.error(e);
      setApiCartData(null);
    }
  };

  const loadApiCartExpiryData: Fetcher<CheckoutApiResponse[], {}> = async () => {
    // Checkout API call
    const result = await getCheckouts();
    if (result?.length && result[0].expiry) {
      setApiCartData(result[0]);
    }
    return result;
  };

  const loadCartData: Fetcher<Awaited<ReturnType<typeof getCarDetail>>, CheckoutApiResponse> = async (apiCartData) => {
    setCarDataLoading();
    const sku = apiCartData.vehicle.most_recently_added_sku;
    const result = await getCarDetail(sku);
    if (result) {
      setCarData(productDetailPayloadToBaseCarData(result));
    }

    return result;
  };

  // Handle loading and refreshing of the cart API data
  const { isLoading: apiCartDataLoading } = useSWR(
    isUserLoggedIn && !userAuthDataLoading ? '/me/checkouts' : null,
    loadApiCartData,
    {
      revalidateOnFocus: true,
      focusThrottleInterval: DEFAULT_FOCUS_THROTTLE,
      refreshInterval: 0,
    },
  );

  // Handle polling of the cart data if cart expiry isn't available
  useSWR(shouldPollCartExpiry(apiCartDataSku, apiCartData ?? null), loadApiCartExpiryData, {
    revalidateOnFocus: true,
    refreshInterval: 2000,
  });

  // Handle loading and mapping of cart data
  useSWR(apiCartData, loadCartData, {
    revalidateOnFocus: true,
    focusThrottleInterval: DEFAULT_FOCUS_THROTTLE,
    refreshInterval: 0,
  });

  useEffect(() => {
    if (!isUserLoggedIn) {
      // clear the cart data
      clearCartData();
      setCartLoadedFirstTime(false);
      setApiCartDataSku(null);
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (apiCartDataLoading && !cartLoadedFirstTime) {
      setCartLoadedFirstTime(true);
      setApiCartDataLoading();
    }
  }, [apiCartDataLoading, cartLoadedFirstTime]);

  useEffect(() => {
    setApiCartDataSku(apiCartData ? apiCartData.vehicle.sku : null);
  }, [apiCartData?.vehicle.sku]);

  return null;
};

const shouldPollCartExpiry = (sku: string | null, apiCartData: CheckoutApiResponse | null): 'checkoutsPoll' | null => {
  return sku && !apiCartData?.expiry ? 'checkoutsPoll' : null;
};
